import { ChangeEvent, ReactNode, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "next-i18next";
import { useCopyIsinModalQuery } from "src/api/client/copy-isin-modal.api";
import { useIsOnWatchlistWikifolio } from "src/api/client/watchlist.api";
import { Typography } from "src/components-mui/base/typography/typography";
import { WfHeading } from "src/components-mui/base/wf-heading/wf-heading";
import { WatchlistButton } from "src/components-mui/common/buttons/watchlist-button/watchlist-button";
import { WF_DIALOG_SPACE_VARIANTS, WfDialog } from "src/components-mui/common/dialog/wf-dialog";
import { SimpleRichText } from "src/components-mui/common/rich-text/simple-rich-text";
import { HIDE_MODALS_KEY, COPY_ISIN_MODAL } from "src/constants/local-storage";
import { useWfToast, ToastStatus } from "src/hooks/use-wf-toast";
import { useUserStore } from "src/stores/user-store";
import { CheckboxWithLabel } from "src/theme-mui/components/checkbox/checkbox-with-label";
import { localStorage } from "src/utils/storage";
import { CopyIsinSkeleton } from "./copy-isin-skeleton";

const ARIA_IDS = {
    TITLE: "copy-isin-dialog-title",
    DESCRIPTION: "copy-isin-dialog-description",
} as const;

interface ICopyIsinDialogProps {
    wikifolioId: string;
    wikifolioShortDescription: string;
    isOnWatchlist: boolean;
    children: (openDialog: () => void) => ReactNode;
}

export const CopyIsinDialog = ({
    wikifolioId,
    wikifolioShortDescription,
    isOnWatchlist: isOnWatchlistFromServer,
    children,
}: ICopyIsinDialogProps) => {
    const { t } = useTranslation("common");
    const toast = useWfToast();
    const { isLoggedIn } = useUserStore();

    const [hideDialog, setHideDialog] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const onCloseDialog = () => {
        if (hideDialog) {
            const hideModals: Array<string | null> = localStorage.get(HIDE_MODALS_KEY) || [];
            localStorage.set(HIDE_MODALS_KEY, [...hideModals, COPY_ISIN_MODAL]);
        }
        setIsOpen(false);
    };

    const { data: dialogText, isLoading, isError } = useCopyIsinModalQuery(isOpen);
    const { headline1, description1, headline2, description2, hideModalCheckbox } = dialogText || {};
    const isOnWatchlist = useIsOnWatchlistWikifolio({ wikifolioId, isOnWatchlistFromServer });

    useEffect(() => {
        if (isError) {
            onCloseDialog();
            toast({ status: ToastStatus.Error, title: t("general-error") });
        }
    }, [isError]); // eslint-disable-line react-hooks/exhaustive-deps

    const openDialog = () => {
        const hideModals: Array<string> | null = localStorage.get(HIDE_MODALS_KEY);

        if (!hideModals?.includes(COPY_ISIN_MODAL) && isLoggedIn && !isOnWatchlist) {
            setIsOpen(true);
        }
    };

    return (
        <>
            {children(openDialog)}
            <WfDialog
                space={WF_DIALOG_SPACE_VARIANTS.MOBILE_SPACIOUS_TOP}
                isOpen={isOpen}
                onClose={onCloseDialog}
                ariaLabelledBy={ARIA_IDS.TITLE}
                ariaDescribedBy={ARIA_IDS.DESCRIPTION}
            >
                {isLoading ? (
                    <CopyIsinSkeleton />
                ) : (
                    <>
                        <Box component="header">
                            <WfHeading component="h2" fontSize={{ xs: "xl", sm: "2xl" }} id={ARIA_IDS.TITLE}>
                                {headline1}
                            </WfHeading>
                            <SimpleRichText
                                text={description1 || ""}
                                id={ARIA_IDS.DESCRIPTION}
                                sx={{
                                    mt: 2,
                                    fontWeight: "normal",
                                    fontSize: "md",
                                }}
                            />
                        </Box>

                        <Box component="section">
                            <Typography
                                component="h3"
                                variant="h6"
                                sx={{
                                    pt: 5,
                                    fontWeight: "bold",
                                    fontSize: { xs: "xl", sm: "2xl" },
                                    mb: 2,
                                }}
                            >
                                {headline2}
                            </Typography>
                            <SimpleRichText text={description2 || ""} sx={{ mt: 2 }} />
                            <Box sx={{ mt: 2 }}>
                                <WatchlistButton
                                    sx={{ width: ["100%", "auto"] }}
                                    size="small"
                                    wikifolioId={wikifolioId}
                                    wikifolioShortDescription={wikifolioShortDescription}
                                    isOnWatchlist={isOnWatchlist}
                                />
                            </Box>
                        </Box>

                        <Box component="footer" sx={{ mt: { xs: "auto", sm: undefined }, pt: 5 }}>
                            <CheckboxWithLabel
                                label={hideModalCheckbox}
                                checkboxProps={{
                                    checked: hideDialog,
                                    onChange: (event: ChangeEvent<HTMLInputElement>) => setHideDialog(event.target.checked),
                                }}
                            />
                        </Box>
                    </>
                )}
            </WfDialog>
        </>
    );
};
